// =====================================================
// PROFESSIONAL JOURNAL LIST COMPONENT - ENHANCED
// Features: Sorting, Pagination, Regime Icons, RR Column
// =====================================================

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { getJournalEntries, deleteJournalEntry } from '@/services/journalService';
import type { JournalEntry, JournalFilters } from '@/types/journal';
import JournalDetailModal from './JournalDetailModal';
import ManualTradeForm from './ManualTradeForm';
import { useDesignVersion } from '@/hooks/useDesignVersion';

const { 
  FiFilter, FiCalendar, FiSearch, FiTrendingUp, FiTrendingDown, 
  FiMinus, FiTrash2, FiEye, FiRefreshCw, FiChevronDown, FiChevronUp,
  FiChevronLeft, FiChevronRight, FiActivity, FiZap, FiPlus
} = FiIcons;

// Pagination configuration
const ITEMS_PER_PAGE = 15;

// Sort configuration
type SortField = 'date' | 'asset' | 'decision' | 'regime' | 'direction' | 'outcome' | 'rr';
type SortDirection = 'asc' | 'desc';

interface JournalListProps {
  onRefresh?: () => void;
}

const JournalList: React.FC<JournalListProps> = ({ onRefresh }) => {
  const { version: designVersion } = useDesignVersion();
  const isV3 = designVersion === 'v3';
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedEntry, setSelectedEntry] = useState<JournalEntry | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showAddTradeForm, setShowAddTradeForm] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  
  // Sort state
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  
  // Filters with debounce
  const [filters, setFilters] = useState<JournalFilters>({
    marketType: 'all',
    assetSymbol: 'all',
    executionPermission: 'all',
    outcomeStatus: 'all',
  });

  // Load entries
  const loadEntries = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getJournalEntries(filters.marketType === 'all' ? undefined : filters);
      setEntries(data);
      setCurrentPage(1); // Reset to first page on refresh
    } catch (error) {
      console.error('Error loading journal entries:', error);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  // Apply client-side filtering, sorting, and pagination
  const processedData = useMemo(() => {
    // 1. Filter
    let filtered = entries.filter(entry => {
      if (filters.marketType !== 'all' && entry.marketType !== filters.marketType) return false;
      if (filters.assetSymbol !== 'all' && entry.assetSymbol !== filters.assetSymbol) return false;
      if (filters.executionPermission !== 'all' && entry.aiDecision.executionPermission !== filters.executionPermission) return false;
      if (filters.outcomeStatus !== 'all' && entry.outcomeStatus !== filters.outcomeStatus) return false;
      return true;
    });

    // 2. Sort
    filtered = [...filtered].sort((a, b) => {
      let comparison = 0;
      
      switch (sortField) {
        case 'date':
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
        case 'asset':
          comparison = a.assetSymbol.localeCompare(b.assetSymbol);
          break;
        case 'decision':
          comparison = (a.aiDecision.executionPermission || '').localeCompare(b.aiDecision.executionPermission || '');
          break;
        case 'regime':
          comparison = (a.aiDecision.marketRegime || '').localeCompare(b.aiDecision.marketRegime || '');
          break;
        case 'direction':
          comparison = (a.aiDecision.direction || '').localeCompare(b.aiDecision.direction || '');
          break;
        case 'outcome':
          comparison = a.outcomeStatus.localeCompare(b.outcomeStatus);
          break;
        case 'rr':
          const rrA = a.outcomeRMultiple ?? a.aiDecision.riskReward ?? 0;
          const rrB = b.outcomeRMultiple ?? b.aiDecision.riskReward ?? 0;
          comparison = rrA - rrB;
          break;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    // 3. Pagination
    const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const paginated = filtered.slice(startIndex, startIndex + ITEMS_PER_PAGE);

    return {
      entries: paginated,
      totalCount: filtered.length,
      totalPages,
    };
  }, [entries, filters, sortField, sortDirection, currentPage]);

  // Get unique assets for filter
  const uniqueAssets = useMemo(() => {
    return [...new Set(entries.map(e => e.assetSymbol))].sort();
  }, [entries]);

  // Handle sort
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
    setCurrentPage(1);
  };

  // Handle delete
  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this journal entry?')) return;
    
    const success = await deleteJournalEntry(id);
    if (success) {
      setEntries(prev => prev.filter(e => e.id !== id));
    }
  };

  // Handle outcome update (from modal)
  const handleOutcomeUpdated = () => {
    loadEntries();
    setSelectedEntry(null);
  };

  // Sort indicator component
  const SortIndicator: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortField !== field) return null;
    return (
      <SafeIcon 
        icon={sortDirection === 'asc' ? FiChevronUp : FiChevronDown} 
        className="w-3 h-3 ml-1 text-cyan-400" 
      />
    );
  };

  // Sortable header
  const SortableHeader: React.FC<{ field: SortField; children: React.ReactNode; align?: 'left' | 'right' | 'center' }> = ({ 
    field, children, align = 'left' 
  }) => (
    <th 
      className={`py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-cyan-400 transition-colors select-none ${
        align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'
      }`}
      onClick={() => handleSort(field)}
    >
      <div className={`flex items-center ${align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : ''}`}>
        {children}
        <SortIndicator field={field} />
      </div>
    </th>
  );

  // Badge components
  const DecisionBadge: React.FC<{ decision: string }> = ({ decision }) => {
    const config: Record<string, { bg: string; text: string; label: string }> = {
      TRADE: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'TRADE' },
      TRADE_APPROVED: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'TRADE' },
      NO_TRADE: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'NO-TRADE' },
      WAIT: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'WAIT' },
      CONDITIONAL: { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'CONDITIONAL' },
      PENDING: { bg: 'bg-gray-500/20', text: 'text-gray-400', label: 'PENDING' },
    };
    const { bg, text, label } = config[decision] || config.PENDING;
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${bg} ${text}`}>
        {label}
      </span>
    );
  };

  // For WAIT / NO_TRADE, direction stored in DB is the AI's BIAS (not an
  // active position) — the counterfactual widget needs it to compute
  // "what would have happened if you'd gone LONG". But surfacing 'LONG' in
  // bright green in the journal table wrongly implies you took the position.
  // Render bias-only decisions in muted gray with a 'bias' prefix so the
  // user immediately reads it as "AI setup direction, no trade taken".
  const DirectionBadge: React.FC<{ direction: string | null; executionPermission?: string | null }> = ({ direction, executionPermission }) => {
    if (!direction || direction === 'NONE') {
      return <span className="text-gray-500 text-sm">—</span>;
    }
    const isLong = direction === 'LONG';
    const isBiasOnly = executionPermission === 'WAIT' || executionPermission === 'NO_TRADE';
    if (isBiasOnly) {
      return (
        <span
          className="inline-flex items-center gap-1 text-xs text-gray-400 font-mono uppercase tracking-[0.1em]"
          title="AI-detected setup bias — you did NOT take this trade"
        >
          <SafeIcon icon={isLong ? FiTrendingUp : FiTrendingDown} className="w-3 h-3 opacity-60" />
          bias {direction.toLowerCase()}
        </span>
      );
    }
    return (
      <span className={`flex items-center gap-1 text-sm font-medium ${isLong ? 'text-emerald-400' : 'text-red-400'}`}>
        <SafeIcon icon={isLong ? FiTrendingUp : FiTrendingDown} className="w-3.5 h-3.5" />
        {direction}
      </span>
    );
  };

  const OutcomeBadge: React.FC<{ status: string }> = ({ status }) => {
    // v3 cleaner labels: 'SKIPPED' -> 'VETOED ✓' (positive framing — the AI
    // actively vetoed the trade), 'AWAITING' -> 'MONITORING' (active state).
    const config: Record<string, { bg: string; text: string; label: string; border?: string }> = {
      WIN:       { bg: 'bg-emerald-500/15', border: 'border-emerald-500/30', text: 'text-emerald-300', label: 'WIN' },
      LOSS:      { bg: 'bg-red-500/15',     border: 'border-red-500/30',     text: 'text-red-300',     label: 'LOSS' },
      BREAKEVEN: { bg: 'bg-blue-500/15',    border: 'border-blue-500/30',    text: 'text-blue-300',    label: 'BREAKEVEN' },
      MISSED:    { bg: 'bg-purple-500/15',  border: 'border-purple-500/30',  text: 'text-purple-300',  label: 'MISSED' },
      CANCELLED: { bg: 'bg-gray-500/15',    border: 'border-white/10',       text: 'text-gray-400',    label: 'CANCELLED' },
      PENDING:   { bg: 'bg-amber-500/15',   border: 'border-amber-500/30',   text: 'text-amber-300',   label: 'PENDING' },
      NO_TRADE:  { bg: 'bg-purple-500/15',  border: 'border-purple-500/30',  text: 'text-purple-300',  label: 'VETOED ✓' },
      WAIT:      { bg: 'bg-cyan-500/15',    border: 'border-cyan-500/30',    text: 'text-cyan-300',    label: 'MONITORING' },
    };
    const { bg, text, label, border } = config[status] || config.PENDING;
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-[0.15em] border ${bg} ${text} ${border || 'border-white/10'}`}>
        {label}
      </span>
    );
  };

  // Enhanced Market Regime Badge with Icons
  const RegimeBadge: React.FC<{ regime: string | null | undefined }> = ({ regime }) => {
    if (!regime) return <span className="text-gray-500 text-sm">—</span>;
    
    // Configuration with icons for each regime type
    const config: Record<string, { bg: string; text: string; icon: React.ComponentType<any> | null }> = {
      'Trend': { bg: 'bg-emerald-500/10', text: 'text-emerald-400', icon: FiTrendingUp },
      'TRENDING_UP': { bg: 'bg-emerald-500/10', text: 'text-emerald-400', icon: FiTrendingUp },
      'TRENDING_DOWN': { bg: 'bg-red-500/10', text: 'text-red-400', icon: FiTrendingDown },
      'Range': { bg: 'bg-blue-500/10', text: 'text-blue-400', icon: FiActivity },
      'RANGING': { bg: 'bg-blue-500/10', text: 'text-blue-400', icon: FiActivity },
      'Distribution': { bg: 'bg-amber-500/10', text: 'text-amber-400', icon: FiTrendingDown },
      'Accumulation': { bg: 'bg-purple-500/10', text: 'text-purple-400', icon: FiTrendingUp },
      'Risk-Off': { bg: 'bg-red-500/10', text: 'text-red-400', icon: FiMinus },
      'HIGH_VOLATILITY': { bg: 'bg-orange-500/10', text: 'text-orange-400', icon: FiZap },
      'Volatility-Expansion': { bg: 'bg-orange-500/10', text: 'text-orange-400', icon: FiZap },
      'UNKNOWN': { bg: 'bg-gray-500/10', text: 'text-gray-400', icon: null },
    };
    
    const { bg, text, icon } = config[regime] || { bg: 'bg-gray-500/10', text: 'text-gray-400', icon: null };
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${bg} ${text}`}>
        {icon && <SafeIcon icon={icon} className="w-3 h-3" />}
        {regime.replace(/_/g, ' ')}
      </span>
    );
  };

  // Net Profit display (using outcomeRMultiple as actual profit/loss value)
  const NetProfitBadge: React.FC<{ entry: JournalEntry }> = ({ entry }) => {
    const netProfit = entry.outcomeRMultiple;
    
    if (netProfit !== null && netProfit !== undefined) {
      const isPositive = netProfit > 0;
      const isNeutral = netProfit === 0;
      return (
        <span className={`font-mono text-sm font-semibold ${
          isPositive ? 'text-emerald-400' : isNeutral ? 'text-blue-400' : 'text-red-400'
        }`}>
          {isPositive ? '+' : netProfit < 0 ? '-' : ''}${Math.abs(netProfit).toLocaleString()}
        </span>
      );
    }
    
    return <span className="text-gray-500 text-sm">—</span>;
  };

  // Format date
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format price
  const formatPrice = (price: number | null) => {
    if (price === null) return '—';
    return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // ═══════════ Summary stats (computed from current entries) ═══════════
  // CRITICAL: this useMemo MUST run on every render — placing it after a
  // conditional return (`if (isLoading)`) violates React Rules of Hooks
  // and crashes the page to a blank screen. Keep it BEFORE any return.
  const summaryStats = useMemo(() => {
    const all = entries || [];
    const total = all.length;
    const executed = all.filter((e: any) => ['WIN', 'LOSS', 'BREAKEVEN'].includes(e.outcomeStatus));
    const wins = executed.filter((e: any) => e.outcomeStatus === 'WIN').length;
    const losses = executed.filter((e: any) => e.outcomeStatus === 'LOSS').length;
    const winRate = (wins + losses) > 0 ? Math.round((wins / (wins + losses)) * 100) : 0;
    const vetoed = all.filter((e: any) => e.outcomeStatus === 'NO_TRADE' || e.outcomeStatus === 'WAIT').length;
    const totalPnL = executed.reduce((sum: number, e: any) => sum + (Number(e.netProfit) || 0), 0);
    return { total, executed: executed.length, vetoed, winRate, totalPnL };
  }, [entries]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500 border-t-transparent" />
        <span className="ml-3 text-gray-400">Loading journal entries...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* ═══════════ V3 SUMMARY STATS HERO ═══════════
          V1 mode hides this entire block (pre-viral design had no hero stats). */}
      {isV3 && (
        <div className="rounded-2xl border border-cyan-400/20 bg-gradient-to-br from-cyan-500/5 via-gray-900/40 to-purple-500/5 p-5">
          <div className="flex items-center justify-between mb-4">
            <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-400">▸ journal · all time</span>
            <button
              onClick={() => alert('PDF export coming soon — Phase 3 feature.')}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 text-gray-300 hover:border-cyan-400/40 hover:text-cyan-300 text-[10px] font-mono uppercase tracking-[0.15em] transition">
              📄 export pdf
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="rounded-xl border border-white/5 bg-white/[0.02] p-3">
              <div className="text-[9px] font-mono uppercase tracking-[0.25em] text-gray-500 mb-1">trades</div>
              <div className="text-2xl font-black font-mono tabular-nums text-white">{summaryStats.total}</div>
            </div>
            <div className="rounded-xl border border-white/5 bg-white/[0.02] p-3">
              <div className="text-[9px] font-mono uppercase tracking-[0.25em] text-gray-500 mb-1">win rate</div>
              <div className={`text-2xl font-black font-mono tabular-nums ${summaryStats.winRate >= 50 ? 'text-emerald-300' : 'text-amber-300'}`}>
                {summaryStats.winRate}%
              </div>
            </div>
            <div className="rounded-xl border border-purple-500/20 bg-purple-500/5 p-3">
              <div className="text-[9px] font-mono uppercase tracking-[0.25em] text-purple-400 mb-1">vetoed (discipline)</div>
              <div className="text-2xl font-black font-mono tabular-nums text-purple-300">{summaryStats.vetoed}</div>
            </div>
            <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-3">
              <div className="text-[9px] font-mono uppercase tracking-[0.25em] text-emerald-400 mb-1">net p&l</div>
              <div className={`text-2xl font-black font-mono tabular-nums ${summaryStats.totalPnL >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                {summaryStats.totalPnL >= 0 ? '+' : ''}${summaryStats.totalPnL.toLocaleString()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header with filters */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-white">Trade History</h3>
          <span className="px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400 text-xs font-medium">
            {processedData.totalCount} entries
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowAddTradeForm(true)}
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-gray-900/60 border border-purple-500/20 hover:border-cyan-500/40 text-cyan-400 text-sm font-medium transition-all hover:bg-gray-900/80"
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4" />
            Add Trade
          </button>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-all ${
              showFilters 
                ? 'bg-gradient-to-r from-purple-600/30 to-cyan-600/30 border border-purple-500/50 text-cyan-400 shadow-lg shadow-purple-500/20' 
                : 'bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 text-cyan-400 hover:from-purple-600/30 hover:to-cyan-600/30 hover:border-purple-500/50 shadow-lg shadow-purple-500/20'
            }`}
          >
            <SafeIcon icon={FiFilter} className="w-4 h-4" />
            Filters
            {Object.values(filters).some(v => v !== 'all') && (
              <span className="w-2 h-2 rounded-full bg-cyan-400" />
            )}
          </button>
          
          <button
            onClick={loadEntries}
            disabled={isLoading}
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 hover:from-purple-600/30 hover:to-cyan-600/30 hover:border-purple-500/50 text-cyan-400 shadow-lg shadow-purple-500/20 text-sm font-medium transition-all disabled:opacity-50"
          >
            <SafeIcon icon={FiRefreshCw} className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters panel */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 bg-gradient-to-r from-purple-500/5 to-cyan-500/5 rounded-xl border border-purple-500/20">
              {/* Market Type */}
              <div>
                <label className="block text-xs bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent mb-1.5 font-medium">Market</label>
                <select
                  value={filters.marketType}
                  onChange={(e) => {
                    setFilters(prev => ({ ...prev, marketType: e.target.value as any }));
                    setCurrentPage(1);
                  }}
                  className="w-full bg-gray-900 border border-purple-500/30 hover:border-cyan-500/50 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-cyan-500 transition-all"
                >
                  <option value="all">All Markets</option>
                  <option value="gold">Gold</option>
                  <option value="indices">Indices</option>
                  <option value="crypto">Crypto</option>
                  <option value="forex">Forex</option>
                </select>
              </div>
              
              {/* Asset */}
              <div>
                <label className="block text-xs bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent mb-1.5 font-medium">Asset</label>
                <select
                  value={filters.assetSymbol}
                  onChange={(e) => {
                    setFilters(prev => ({ ...prev, assetSymbol: e.target.value }));
                    setCurrentPage(1);
                  }}
                  className="w-full bg-gray-900 border border-purple-500/30 hover:border-cyan-500/50 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-cyan-500 transition-all"
                >
                  <option value="all">All Assets</option>
                  {uniqueAssets.map(asset => (
                    <option key={asset} value={asset}>{asset}</option>
                  ))}
                </select>
              </div>
              
              {/* Decision */}
              <div>
                <label className="block text-xs bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent mb-1.5 font-medium">Decision</label>
                <select
                  value={filters.executionPermission}
                  onChange={(e) => {
                    setFilters(prev => ({ ...prev, executionPermission: e.target.value as any }));
                    setCurrentPage(1);
                  }}
                  className="w-full bg-gray-900 border border-purple-500/30 hover:border-cyan-500/50 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-cyan-500 transition-all"
                >
                  <option value="all">All Decisions</option>
                  <option value="TRADE">TRADE</option>
                  <option value="NO_TRADE">NO-TRADE</option>
                  <option value="WAIT">WAIT</option>
                  <option value="CONDITIONAL">CONDITIONAL</option>
                </select>
              </div>
              
              {/* Outcome */}
              <div>
                <label className="block text-xs bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent mb-1.5 font-medium">Outcome</label>
                <select
                  value={filters.outcomeStatus}
                  onChange={(e) => {
                    setFilters(prev => ({ ...prev, outcomeStatus: e.target.value as any }));
                    setCurrentPage(1);
                  }}
                  className="w-full bg-gray-900 border border-purple-500/30 hover:border-cyan-500/50 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-cyan-500 transition-all"
                >
                  <option value="all">All Outcomes</option>
                  <option value="PENDING">Pending</option>
                  <option value="WIN">Win</option>
                  <option value="LOSS">Loss</option>
                  <option value="BREAKEVEN">Breakeven</option>
                  <option value="MISSED">Missed</option>
                  <option value="CANCELLED">Cancelled</option>
                  <option value="NO_TRADE">Skipped (No-Trade)</option>
                  <option value="WAIT">Awaiting (Wait)</option>
                </select>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Empty state with CTA */}
      {entries.length === 0 && (
        <div className="text-center py-16">
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 flex items-center justify-center">
            <SafeIcon icon={FiSearch} className="w-10 h-10 text-purple-400" />
          </div>
          <h4 className="text-xl font-semibold text-white mb-2">No Journal Entries Yet</h4>
          <p className="text-gray-400 text-sm mb-6 max-w-md mx-auto">
            Start analyzing charts with Snap Chart AI to automatically log your trading decisions and build your discipline score.
          </p>
          <button
            onClick={() => {
              // Close journal and navigate to trading
              window.dispatchEvent(new CustomEvent('closeJournal'));
            }}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-cyan-600 text-white font-semibold hover:from-purple-500 hover:to-cyan-500 transition-all shadow-lg shadow-purple-500/30"
          >
            <SafeIcon icon={FiPlus} className="w-5 h-5" />
            Analyze Your First Chart
          </button>
        </div>
      )}

      {/* No results from filter */}
      {entries.length > 0 && processedData.totalCount === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gray-800 flex items-center justify-center">
            <SafeIcon icon={FiFilter} className="w-8 h-8 text-gray-600" />
          </div>
          <h4 className="text-lg font-medium text-white mb-1">No matching entries</h4>
          <p className="text-gray-500 text-sm">Try adjusting your filters to see more results</p>
          <button
            onClick={() => {
              setFilters({
                marketType: 'all',
                assetSymbol: 'all',
                executionPermission: 'all',
                outcomeStatus: 'all',
              });
              setCurrentPage(1);
            }}
            className="mt-4 px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-cyan-400 text-sm font-medium transition-colors"
          >
            Clear Filters
          </button>
        </div>
      )}

      {/* Table */}
      {processedData.totalCount > 0 && (
        <>
          <div className="overflow-x-auto rounded-xl border border-white/5">
            <table className="w-full">
              <thead className="bg-gray-800/50">
                <tr className="border-b border-white/5">
                  <SortableHeader field="date">Date</SortableHeader>
                  <SortableHeader field="asset">Asset</SortableHeader>
                  <SortableHeader field="decision">Decision</SortableHeader>
                  <SortableHeader field="regime">Regime</SortableHeader>
                  <SortableHeader field="direction">Direction</SortableHeader>
                  <th className="py-3 px-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Entry</th>
                  <SortableHeader field="outcome" align="center">Outcome</SortableHeader>
                  <SortableHeader field="rr" align="right">Net Profit / Net Loss</SortableHeader>
                  <th className="py-3 px-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {processedData.entries.map((entry) => (
                  <motion.tr
                    key={entry.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-white/[0.02] cursor-pointer transition-colors group"
                    onClick={() => setSelectedEntry(entry)}
                    title={entry.aiDecision.primaryReason || undefined}
                  >
                    <td className="py-3 px-4 text-sm text-gray-400">{formatDate(entry.createdAt)}</td>
                    <td className="py-3 px-4">
                      <div>
                        <span className="text-sm font-medium text-white">{entry.assetSymbol}</span>
                        {/* Market label auto-detected from symbol (symbol = source of truth).
                            Avoids stale stored marketType showing 'gold' next to BTCUSD. */}
                        <span className="text-xs text-gray-500 ml-2 capitalize">
                          {(() => {
                            const up = (entry.assetSymbol || '').toUpperCase();
                            const CRYPTO_BASES = ['BTC','ETH','SOL','XRP','ADA','DOGE','AVAX','LINK','MATIC','DOT'];
                            const INDICES = ['US30','NAS100','SPP500','GER30'];
                            if (up.startsWith('XAU')) return 'gold';
                            if (CRYPTO_BASES.some((b) => up.includes(b))) return 'crypto';
                            if (INDICES.includes(up)) return 'indices';
                            return entry.marketType;
                          })()}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex flex-col gap-1">
                        <DecisionBadge decision={entry.aiDecision.executionPermission} />
                        {entry.aiDecision.primaryReason && (
                          <span className="text-[10px] text-gray-500 max-w-[120px] truncate opacity-0 group-hover:opacity-100 transition-opacity">
                            {entry.aiDecision.primaryReason}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <RegimeBadge regime={entry.aiDecision.marketRegime} />
                    </td>
                    <td className="py-3 px-4">
                      <DirectionBadge direction={entry.aiDecision.direction} executionPermission={entry.aiDecision.executionPermission} />
                    </td>
                    <td className="py-3 px-4 text-right text-sm text-white tabular-nums">
                      {formatPrice(entry.aiDecision.entry)}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <OutcomeBadge status={entry.outcomeStatus} />
                    </td>
                    <td className="py-3 px-4 text-right">
                      <NetProfitBadge entry={entry} />
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); setSelectedEntry(entry); }}
                          className="p-1.5 rounded-lg hover:bg-white/5 text-gray-500 hover:text-white transition-colors"
                          title="View details"
                        >
                          <SafeIcon icon={FiEye} className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(entry.id); }}
                          className="p-1.5 rounded-lg hover:bg-red-500/10 text-gray-500 hover:text-red-400 transition-colors"
                          title="Delete entry"
                        >
                          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {processedData.totalPages > 1 && (
            <div className="flex items-center justify-between pt-4">
              <p className="text-sm text-gray-500">
                Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, processedData.totalCount)} of {processedData.totalCount}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-400 hover:text-white transition-colors"
                >
                  <SafeIcon icon={FiChevronLeft} className="w-4 h-4" />
                </button>
                
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, processedData.totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (processedData.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= processedData.totalPages - 2) {
                      pageNum = processedData.totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                          currentPage === pageNum
                            ? 'bg-gradient-to-r from-purple-600 to-cyan-600 text-white'
                            : 'bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => setCurrentPage(prev => Math.min(processedData.totalPages, prev + 1))}
                  disabled={currentPage === processedData.totalPages}
                  className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-400 hover:text-white transition-colors"
                >
                  <SafeIcon icon={FiChevronRight} className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Detail Modal */}
      {selectedEntry && (
        <JournalDetailModal
          entry={selectedEntry}
          onClose={() => setSelectedEntry(null)}
          onOutcomeUpdated={handleOutcomeUpdated}
        />
      )}

      {/* Manual Trade Form */}
      <ManualTradeForm
        isOpen={showAddTradeForm}
        onClose={() => setShowAddTradeForm(false)}
        onSuccess={loadEntries}
      />
    </div>
  );
};

export default JournalList;
